import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";

export interface StorySlide {
  id?: number;
  imageUrl?: string;
  videoUrl?: string;
  linkUrl?: string;
  linkText?: string;
  content: string;
  order: number;
}

export interface Story {
  id: number;
  title: string;
  subtitle: string | null;
  category: string;
  coverImage: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  slides: StorySlide[];
  _count: { likes: number; saves: number };
}

export interface CreateStoryPayload {
  title: string;
  subtitle?: string;
  category?: string;
  coverImage?: string;
  isPublished?: boolean;
  slides?: Omit<StorySlide, "id">[];
}

const storyKeys = {
  all: ["stories"] as const,
  list: () => [...storyKeys.all, "list"] as const,
};

export function useStories() {
  return useQuery<Story[]>({
    queryKey: storyKeys.list(),
    queryFn: () => apiClient.get(API_ENDPOINTS.STORIES.LIST),
  });
}

export function useCreateStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateStoryPayload) =>
      apiClient.post(API_ENDPOINTS.STORIES.CREATE, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export function useUpdateStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateStoryPayload> }) =>
      apiClient.patch(API_ENDPOINTS.STORIES.UPDATE(id), data),
    onSuccess: () => qc.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export function useToggleStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.post(API_ENDPOINTS.STORIES.TOGGLE(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export function useDeleteStory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(API_ENDPOINTS.STORIES.DELETE(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export async function uploadStoryImage(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append("file", file);
  return apiClient.post(API_ENDPOINTS.STORIES.UPLOAD_IMAGE, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}
